import { LocalizedString } from 'hic-components'
import { ChildrenQuery, PricingType } from 'src/api/api.generated'

export function calculateAdditionalServicePrice({
  adults,
  children,
  price,
  days,
  pricingType,
  automaticForAdults,
  automaticForChildren,
  quantity,
  childPrices,
  priceZero,
}: {
  adults: number
  children: { childId: string; quantity: number }[]
  price: number
  pricingType: PricingType
  days: number
  automaticForAdults: boolean
  automaticForChildren: boolean
  priceZero: boolean
  quantity?: number | null
  childPrices?:
    | {
        childId: string
        price: number
      }[]
    | null
}) {
  const adultsCount = automaticForAdults ? adults : 0

  function getChildPrice(childId: string) {
    const priceOverride = childPrices?.find((child) => child.childId === childId)?.price
    return priceOverride ?? 0
  }

  if (priceZero) {
    return 0
  }

  switch (pricingType) {
    case PricingType.PER_STAY:
      return price * (quantity ?? 1)
    case PricingType.PER_PERSON:
      return price * adultsCount + (automaticForChildren ? children.map((child) => getChildPrice(child.childId) * child.quantity).reduce((prev, curr) => prev + curr, 0) : 0)
    case PricingType.PER_NIGHT:
      return price * days * (quantity ?? 1)
    case PricingType.PER_PERSON_PER_NIGHT:
      return price * days * adultsCount + (automaticForChildren ? children.map((child) => getChildPrice(child.childId) * child.quantity * days).reduce((prev, curr) => prev + curr, 0) : 0)
  }
}

export function calculateAdditionalServiceQuantity({
  adults,
  children,
  days,
  pricingType,
  automaticForAdults,
  automaticForChildren,
  quantity,
}: {
  adults: number
  children: number
  pricingType: PricingType
  days: number
  automaticForAdults: boolean
  automaticForChildren: boolean
  quantity?: number | null
}) {
  const adultsCount = automaticForAdults ? adults : 0
  const childrenCount = automaticForChildren ? children : 0
  switch (pricingType) {
    case PricingType.PER_STAY:
      return quantity ?? 1
    case PricingType.PER_PERSON:
      return adultsCount + childrenCount
    case PricingType.PER_NIGHT:
      return days * (quantity ?? 1)
    case PricingType.PER_PERSON_PER_NIGHT:
      return days * adultsCount + days * childrenCount
  }
}

export function calculateAdditionalServiceQuantityWithPrice({
  adults,
  children,
  price,
  days,
  pricingType,
  automaticForAdults,
  automaticForChildren,
  quantity,
  childPrices,
  childrenResult,
}: {
  adults: number
  children: { childId: string; quantity: number }[]
  price: number
  pricingType: PricingType
  days: number
  automaticForAdults: boolean
  automaticForChildren: boolean
  quantity?: number | null
  childPrices?:
    | {
        childId: string
        price: number
      }[]
    | null
  childrenResult: ChildrenQuery | undefined
}): { childTaxonomy?: LocalizedString; quantity: number; price: number }[] {
  const adultsCount = automaticForAdults ? adults : 0

  function getChildPrice(childId: string) {
    const priceOverride = childPrices?.find((child) => child.childId === childId)?.price
    return priceOverride ?? 0
  }

  switch (pricingType) {
    case PricingType.PER_STAY:
      return [{ price: price, quantity: quantity ?? 1 }]
    case PricingType.PER_PERSON: {
      const tmp = children.map((child) => {
        const taxonomyDescription = childrenResult?.children?.find((c) => c.id === child.childId)?.taxonomy.description
        return { childTaxonomy: taxonomyDescription, price: getChildPrice(child.childId), quantity: child.quantity }
      })
      return [{ price: price, quantity: adultsCount }, ...(automaticForChildren ? tmp : [])]
    }
    case PricingType.PER_NIGHT:
      return [{ price: price, quantity: days * (quantity ?? 1) }]
    case PricingType.PER_PERSON_PER_NIGHT: {
      const tmp = children.map((child) => {
        const taxonomyDescription = childrenResult?.children?.find((c) => c.id === child.childId)?.taxonomy.description
        return { childTaxonomy: taxonomyDescription, price: getChildPrice(child.childId), quantity: child.quantity * days }
      })
      return [{ price: price, quantity: days * adultsCount }, ...(automaticForChildren ? tmp : [])]
    }
  }
}
